import {StyleSheet} from "aphrodite";
import React from "react";

import {Text, View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";

import {type JsonObject, type JsonPath, type JsonValue} from "../../types.ts";
import {useCoeditingState} from "../../use-coediting-state.ts";

import SparkleButton from "./sparkle-button.tsx";

function JsonStringView(props: {path: JsonPath; data: string}) {
    const {path, data} = props;

    return (
        <Text style={styles.string}>
            {JSON.stringify(data)} <SparkleButton path={path} />
        </Text>
    );
}

function JsonNumberView(props: {path: JsonPath; data: number}) {
    const {path, data} = props;

    return (
        <Text style={styles.number}>
            {String(data)} <SparkleButton path={path} />
        </Text>
    );
}

function JsonBooleanView(props: {path: JsonPath; data: boolean}) {
    const {path, data} = props;

    return (
        <Text style={styles.boolean}>
            {String(data)} <SparkleButton path={path} />
        </Text>
    );
}

function JsonNullView(props: {path: JsonPath; data: null}) {
    const {path, data} = props;

    return (
        <Text style={styles.null}>
            {String(data)} <SparkleButton path={path} />
        </Text>
    );
}

function JsonPropertyValuePair(props: {
    path: JsonPath;
    depth: number;
    property: string | number;
    value: JsonValue;
}) {
    const {path, depth, property, value} = props;

    const [start, end] = Array.isArray(value) ? ["[", "]"] : ["{", "}"];

    if (Array.isArray(value)) {
        return (
            <View>
                <View style={styles.propertyValuePair}>
                    <Text style={styles.property}>{String(property)}: </Text>
                    <Strut size={spacing.xxSmall_6} />
                    <Text style={styles.delimiter}>{start}</Text>
                    <Strut size={spacing.xSmall_8} />
                    <SparkleButton path={path} />
                </View>
                <Strut size={spacing.xSmall_8} />
                <JsonArrayView path={path} data={value} depth={depth + 1} />
                <View>
                    <Text style={styles.delimiter}>{end}</Text>
                </View>
            </View>
        );
    }

    if (value && typeof value === "object") {
        return (
            <View>
                <View style={styles.propertyValuePair}>
                    <Text style={styles.property}>{String(property)}: </Text>
                    <Strut size={spacing.xxSmall_6} />
                    <Text style={styles.delimiter}>{start}</Text>
                    <Strut size={spacing.xSmall_8} />
                    <SparkleButton path={path} />
                </View>
                <Strut size={spacing.xSmall_8} />
                <JsonObjectView path={path} data={value} depth={depth + 1} />
                <View>
                    <Text style={styles.delimiter}>{end}</Text>
                </View>
            </View>
        );
    }

    let rendered: React.ReactNode = null;
    switch (typeof value) {
        case "string":
            rendered = <JsonStringView data={value} path={path} />;
            break;
        case "number":
            rendered = <JsonNumberView data={value} path={path} />;
            break;
        case "boolean":
            rendered = <JsonBooleanView data={value} path={path} />;
            break;
        default:
            rendered = <JsonNullView data={value} path={path} />;
            break;
    }

    return (
        <View style={styles.propertyValuePair}>
            <Text style={styles.property}>{String(property)}: </Text>
            <Strut size={spacing.xxSmall_6} />
            {rendered}
        </View>
    );
}

function JsonArrayView(props: {
    path: JsonPath;
    depth: number;
    data: JsonValue[];
}) {
    const {path, depth, data} = props;

    const elements: React.ReactElement[] = [];

    for (const value of data) {
        const index = elements.length;

        elements.push(
            <JsonPropertyValuePair
                key={index}
                depth={depth}
                property={index}
                value={value}
                path={[...path, String(index)]}
            />,
        );
    }

    elements.push(
        <View key="add-item">
            <SparkleButton
                label="Add item"
                path={[...path, String(elements.length)]}
            />
        </View>,
    );

    return <View style={styles.array}>{elements}</View>;
}

function JsonObjectView(props: {
    path: JsonPath;
    depth: number;
    data: JsonObject;
}) {
    const {path, depth, data} = props;

    const entries: React.ReactElement[] = [];

    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            entries.push(
                <JsonPropertyValuePair
                    key={key}
                    depth={depth}
                    property={key}
                    value={data[key]}
                    path={[...path, key]}
                />,
            );
        }
    }

    return <View style={styles.object}>{entries}</View>;
}

export default function JsonTree(props: {depth?: number}) {
    const {depth = -1} = props;

    const {data} = useCoeditingState();

    const path: JsonPath = [];

    if (data == null) {
        return <JsonNullView path={path} data={data} />;
    }

    switch (typeof data) {
        case "string":
            return <JsonStringView path={path} data={data} />;
        case "number":
            return <JsonNumberView path={path} data={data} />;
        case "boolean":
            return <JsonBooleanView path={path} data={data} />;
        default:
            if (Array.isArray(data)) {
                return (
                    <JsonArrayView path={path} data={data} depth={depth + 1} />
                );
            }

            return <JsonObjectView path={path} data={data} depth={depth + 1} />;
    }
}

const styles = StyleSheet.create({
    object: {
        borderInlineStart: `1px solid ${color.offBlack8}`,
        paddingInlineStart: spacing.medium_16,
        flexShrink: 0,
        gap: spacing.small_12,
    },
    array: {
        borderInlineStart: `1px solid ${color.offBlack8}`,
        paddingInlineStart: spacing.medium_16,
        flexShrink: 0,
        gap: spacing.small_12,
    },
    property: {
        color: color.offBlack,
        fontWeight: "bold",
    },
    propertyValuePair: {
        flexDirection: "row",
        alignItems: "center",
    },
    string: {
        color: color.blue,
    },
    number: {
        color: color.green,
    },
    boolean: {
        color: color.purple,
    },
    null: {
        color: color.teal,
    },
    delimiter: {
        fontWeight: "bold",
    },
});
